import React, { useState, useRef } from 'react';
import { QrCode, Globe, Leaf } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { useLanguage } from '../components/LanguageContext';
import { FlatSeed } from '../components/FlatSeed';
import { FlatSprout } from '../components/FlatSprout';
import { FlatTree } from '../components/FlatTree';
import { FlatForest } from '../components/FlatForest';
// ────────────────────────────────────────────────────────────────
// ⚠️  DEMO DATA — da sostituire in produzione
// ────────────────────────────────────────────────────────────────
// Questi numeri sono hardcoded solo per il prototipo. Nell'app reale
// dovranno essere fetchati dal backend:
//   - personal.connections → count delle connessioni dell'utente loggato
//     (vedere tabella `connections` nella doc tecnica §4).
//   - community.* → aggregate queries realtime su tutta la user-base
//     (vedere doc tecnica §7).
// I valori qui sono scelti per essere plausibili pre-Summit, non vuoti
// (che farebbero sembrare l'app morta) ma nemmeno inflazionati.
// ────────────────────────────────────────────────────────────────
const USE_DEMO_STATS = true;
const DEMO_STATS = {
  personal: {
    connections: 3
  },
  community: {
    connections: 23,
    trees: 0,
    participants: 48
  }
};
const EMPTY_STATS = {
  personal: {
    connections: 0
  },
  community: {
    connections: 0,
    trees: 0,
    participants: 0
  }
};
interface Props {
  onNavigateToMatch: () => void;
  onScanPress: () => void;
}
export function TreeHome({
  onNavigateToMatch,
  onScanPress
}: Props) {
  const {
    t
  } = useLanguage();
  const stats = USE_DEMO_STATS ? DEMO_STATS : EMPTY_STATS;
  const personalConnections = stats.personal.connections;
  const [activeSlide, setActiveSlide] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const isHorizontalSwipe = useRef<boolean | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    isHorizontalSwipe.current = null;
    setIsDragging(true);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const diffX = e.touches[0].clientX - startX.current;
    const diffY = e.touches[0].clientY - startY.current;
    if (isHorizontalSwipe.current === null) {
      if (Math.abs(diffX) > 5 || Math.abs(diffY) > 5) {
        isHorizontalSwipe.current = Math.abs(diffX) > Math.abs(diffY);
      }
      return;
    }
    if (!isHorizontalSwipe.current) return;
    if (e.cancelable) e.preventDefault();
    if (activeSlide === 0 && diffX > 0) {
      setDragOffset(diffX * 0.3);
    } else if (activeSlide === 1 && diffX < 0) {
      setDragOffset(diffX * 0.3);
    } else {
      setDragOffset(diffX);
    }
  };
  const handleTouchEnd = () => {
    setIsDragging(false);
    isHorizontalSwipe.current = null;
    if (dragOffset < -60 && activeSlide === 0) setActiveSlide(1);else if (dragOffset > 60 && activeSlide === 1) setActiveSlide(0);
    setDragOffset(0);
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    startX.current = e.clientX;
    startY.current = e.clientY;
    isHorizontalSwipe.current = null;
    setIsDragging(true);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const diffX = e.clientX - startX.current;
    const diffY = e.clientY - startY.current;
    if (isHorizontalSwipe.current === null) {
      if (Math.abs(diffX) > 5 || Math.abs(diffY) > 5) {
        isHorizontalSwipe.current = Math.abs(diffX) > Math.abs(diffY);
      }
      return;
    }
    if (!isHorizontalSwipe.current) return;
    if (activeSlide === 0 && diffX > 0) setDragOffset(diffX * 0.3);else if (activeSlide === 1 && diffX < 0) setDragOffset(diffX * 0.3);else setDragOffset(diffX);
  };
  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    isHorizontalSwipe.current = null;
    if (dragOffset < -60 && activeSlide === 0) setActiveSlide(1);else if (dragOffset > 60 && activeSlide === 1) setActiveSlide(0);
    setDragOffset(0);
  };
  const translateX = -activeSlide * 100;
  const dragPercent = dragOffset / window.innerWidth * 100;
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] overflow-hidden relative pb-[72px]" data-id="element-1030">
      {/* Unified Header */}
      <div className="relative z-20 bg-[#0D0D0D]" data-id="element-1031">
        <PageHeader title={t('nav.impact')} data-id="element-1032" />
      </div>

      {/* Image Container */}
      <div className="flex-1 relative z-10" data-id="element-1033">
        <div className="absolute inset-0" data-id="element-1034">
          <img src="https://cdn.magicpatterns.com/uploads/wAz8Z9b8KzVxnXJuJ2bj5o/simon-wilkes-S297j2CsdlM-unsplash.jpg" alt="Forest" className="w-full h-full object-cover" data-id="element-1035" />
          <div className="absolute inset-0" style={{
          background: 'linear-gradient(to top, #0D0D0D 0%, #0D0D0D 15%, rgba(13,13,13,0.85) 35%, transparent 60%)'
        }} data-id="element-1036" />
        </div>

        {/* Content overlay */}
        <div className="absolute inset-0 flex flex-col justify-end pb-4" data-id="element-1037">
          {/* Slides Container */}
          <div className="w-full overflow-hidden relative" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} data-id="element-1038">
            <div className="flex w-full items-end" style={{
            transform: `translateX(calc(${translateX}% + ${dragPercent}%))`,
            transition: isDragging ? 'none' : 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            cursor: isDragging ? 'grabbing' : 'grab'
          }} data-id="element-1039">
              {/* SLIDE 1: Your impact */}
              <div className="w-full flex-shrink-0 px-6 pb-4 flex flex-col items-center justify-start" data-id="element-1040">
                <div className="inline-flex items-center gap-1.5 bg-[#0D0D0D]/60 backdrop-blur-md border border-white/10 rounded-full px-3 py-1 mb-3" data-id="element-1041">
                  <Leaf className="w-3.5 h-3.5 text-[#C5D82D]" strokeWidth={2} data-id="element-1042" />
                  <span className="text-[11px] text-[#C5D82D] font-semibold uppercase tracking-widest" data-id="element-1043">
                    {t('tree.yourImpact')}
                  </span>
                </div>

                <div className="flex flex-col items-center mb-3" data-id="element-1044">
                  <span className="text-[56px] text-white leading-none font-semibold tabular-nums drop-shadow-lg" data-id="element-1045">
                    {personalConnections}
                  </span>
                  <span className="text-[15px] text-white/90 font-medium drop-shadow-md mt-1" data-id="element-1046">
                    {t('tree.connections')}
                  </span>
                </div>
              </div>

              {/* SLIDE 2: Community impact */}
              <div className="w-full flex-shrink-0 px-6 pb-4 flex flex-col items-center justify-center" data-id="element-1047">
                <div className="inline-flex items-center gap-1.5 bg-[#0D0D0D]/60 backdrop-blur-md border border-white/10 rounded-full px-3 py-1 mb-4" data-id="element-1048">
                  <Globe className="w-3.5 h-3.5 text-[#C5D82D]" strokeWidth={2} data-id="element-1049" />
                  <span className="text-[11px] text-[#C5D82D] font-semibold uppercase tracking-widest" data-id="element-1050">
                    {t('tree.communityImpact')}
                  </span>
                </div>

                <div className="flex items-center justify-between w-full mb-3" data-id="element-1051">
                  <div className="flex flex-col items-start" data-id="element-1052">
                    <span className="text-[28px] text-white leading-none font-semibold tabular-nums drop-shadow-lg" data-id="element-1053">
                      {stats.community.connections}
                    </span>
                    <span className="text-[12px] text-white/80 mt-1 font-medium" data-id="element-1054">
                      {t('tree.connections')}
                    </span>
                  </div>
                  <div className="w-[1px] h-8 bg-white/20" data-id="element-1055" />
                  <div className="flex flex-col items-start" data-id="element-1056">
                    <span className="text-[28px] text-white leading-none font-semibold tabular-nums drop-shadow-lg" data-id="element-1057">
                      {stats.community.trees}
                    </span>
                    <span className="text-[12px] text-white/80 mt-1 font-medium" data-id="element-1058">
                      {t('tree.trees')}
                    </span>
                  </div>
                  <div className="w-[1px] h-8 bg-white/20" data-id="element-1059" />
                  <div className="flex flex-col items-start" data-id="element-1060">
                    <span className="text-[28px] text-white leading-none font-semibold tabular-nums drop-shadow-lg" data-id="element-1061">
                      {stats.community.participants}
                    </span>
                    <span className="text-[12px] text-white/80 mt-1 font-medium" data-id="element-1062">
                      {t('tree.participants')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-2 pb-5" data-id="element-1063">
            <button onClick={() => setActiveSlide(0)} aria-label="Slide 1" className={`h-1.5 rounded-full transition-all duration-300 ${activeSlide === 0 ? 'w-6 bg-[#C5D82D]' : 'w-2 bg-white/30'}`} data-id="element-1064" />
            <button onClick={() => setActiveSlide(1)} aria-label="Slide 2" className={`h-1.5 rounded-full transition-all duration-300 ${activeSlide === 1 ? 'w-6 bg-[#C5D82D]' : 'w-2 bg-white/30'}`} data-id="element-1065" />
          </div>

          {/* Fixed Bottom CTA */}
          <div className="px-6" data-id="element-1066">
            <button onClick={onNavigateToMatch} className="w-full py-4 flex items-center justify-center rounded-full border-[1.5px] border-[#C5D82D] text-[#C5D82D] bg-[#0D0D0D]/50 backdrop-blur-sm text-[14px] font-semibold tracking-wide transition-all duration-150 hover:bg-[#C5D82D]/10 active:bg-[#C5D82D]/20 active:scale-[0.98]" data-id="element-1067">
              {activeSlide === 0 ? t('tree.cta.goToConnections') : t('tree.cta.contributeToo')}
            </button>
          </div>
        </div>
      </div>
    </div>;
}